import { toExcelAmount, toExcelDate, toExcelDateTime, toExcelQuantity } from 'components/xlsx/excelCells';
import { ClassifierData } from 'types/classifiers';
import { ExportMapperOptions } from 'types/exports';
import { TimecardListExportItemDto } from 'types/timecards';
import { getClassifierText, t } from 'utils/getTranslations';
import { timecardSecondsToHours } from '../utils/timecardUtils';

export const mapTimecardsToCSVFormat = (
    timecards: TimecardListExportItemDto[],
    classifiers: Record<string, ClassifierData>,
    options?: ExportMapperOptions
) => {
    const isTwoDecimalEnabled = options?.isTwoDecimalEnabled ?? false;
    // The two uplift columns only exist when TIMECARD_BILLED_TIME_CONTROL is on, and they go last.
    const upliftColumns = (timecard: TimecardListExportItemDto) =>
        options?.isBilledTimeControlEnabled
            ? {
                  [t('timecards.export.billing-uplift')]:
                      (timecard.billedTimeInSeconds ?? 0) > (timecard.totalTimeInSeconds ?? 0),
                  [t('timecards.export.value-factors')]: (timecard.billingUpliftValueFactors ?? [])
                      .map((code) => getClassifierText(classifiers['TIMECARD_VALUE_FACTORS'], code))
                      .join(', '),
              }
            : {};
    return timecards?.map((timecard) => ({
        Id: timecard.id,
        [t('timecards.export.connected-client')]: timecard.matter.clientParty.name,
        [t('timecards.export.case-name')]: timecard.matter.fullName,
        [t('timecards.export.case-status')]: getClassifierText(
            classifiers['MATTER_STATE_TYPE'],
            timecard.matter.matterStateTypeCode
        ),
        [t('timecards.export.case-fee-arrangement')]: getClassifierText(
            classifiers['PRICING_LIMITATION_TYPE'],
            timecard.matter.pricingLimitationTypeCode
        ),
        [t('timecards.export.assignment')]: timecard.matterAssignment?.name,
        [t('timecards.export.connected-invoice')]: timecard.invoice?.invoiceNumber || t('not-available'),
        [t('timecards.export.total-invoice-fees-before-vat')]: toExcelAmount(timecard.invoice?.feesAfterAdjustments),
        [t('timecards.export.timecard-status')]: getClassifierText(
            classifiers['TIMECARD_STATUS_TYPE'],
            timecard.timecardStatusTypeCode
        ),
        [t('timecards.export.timecard-date')]: toExcelDate(timecard.timecardDate),
        [t('timecards.export.created-at')]: toExcelDateTime(timecard.createdAt),
        [t('timecards.export.timecard-author')]: timecard.employee.name,
        [t('timecards.export.job-role')]: timecard.employment?.jobRole?.name,
        [t('timecards.export.author-team')]: timecard.employment?.coreTeam?.name,
        [t('timecards.export.belongs-to-company')]: timecard.employment?.company?.name,
        [t('timecards.export.details-of-work')]: timecard.description,
        [t('timecards.export.timecard-language')]: getClassifierText(
            classifiers['LANGUAGE'],
            timecard.matter.timecardLanguageCode
        ),
        [t('timecards.export.used-time')]: toExcelQuantity(
            timecardSecondsToHours(timecard.totalTimeInSeconds ?? 0, isTwoDecimalEnabled)
        ),
        [t('timecards.export.billed-time')]: toExcelQuantity(
            timecardSecondsToHours(timecard.billedTimeInSeconds ?? 0, isTwoDecimalEnabled)
        ),
        [t('timecards.export.invoiced-billed-time')]: toExcelQuantity(
            timecard.isNonBillable ? 0 : timecardSecondsToHours(timecard.billedTimeInSeconds ?? 0, isTwoDecimalEnabled)
        ),
        [t('timecards.export.non-billable')]: timecard.isNonBillable,
        [t('timecards.export.dont-nullify-billed-hours')]: timecard.preserveNonBillableHours,
        [t('timecards.export.timecard-amount-in-price-list-currency')]: toExcelAmount(
            timecard.amountInOriginalCurrency
        ),
        [t('timecards.export.timecard-invoiced-amount-in-price-list-currency')]: toExcelAmount(
            timecard.isNonBillable ? 0 : timecard.invoiceTimecardDetails?.totalAmount
        ),
        [t('timecards.export.timecard-invoiced-amount-in-invoice-currency')]: toExcelAmount(
            timecard.isNonBillable ? 0 : timecard.invoiceTimecardDetails?.amountInPaymentCurrency
        ),
        [t('timecards.export.timecard-invoiced-amount-in-base-currency')]: toExcelAmount(
            timecard.isNonBillable ? 0 : timecard.invoiceTimecardDetails?.amountInBaseCurrency
        ),
        [t('timecards.export.hourly-rate')]: toExcelAmount(timecard.invoiceTimecardDetails?.hourlyPrice),
        [t('timecards.export.price-list-currency')]: timecard.invoiceTimecardDetails?.originalCurrencyTypeCode,
        [t('timecards.export.invoice-currency')]:
            timecard?.invoiceTimecardDetails?.paymentCurrencyTypeCode || t('not-available'),
        [t('timecards.export.multiplier')]: toExcelQuantity(timecard.multiplierRate),
        [t('timecards.export.practice-area')]: timecard.practiceAreas
            .map((practiceArea) => practiceArea.name)
            .join(', '),
        [t('timecards.export.timecard-invoiced-author')]:
            timecard.invoiceTimecardDetails?.employee?.name || t('not-available'),
        [t('timecards.export.invoiced-details-of-work')]:
            timecard.invoiceTimecardDetails?.description || t('not-available'),
        ...upliftColumns(timecard),
    }));
};
